import { getDealRegularDocumentTypes, getDocumentTypeText } from '../../../../../types/amr-pipeline/enums/DocumentType';
import { Deal } from '../../../../../types/amr-pipeline/models/Deal';
import { SearchInput } from '../../../../common';
import { TabBody } from '../../../common/tabs/TabBody';
import { DocumentType } from '../../../../../types/amr-pipeline/enums/DocumentType';
import { useState } from 'react';
import { DocumentSection } from '../../../common/documents/DocumentSection';
import { Transaction } from '../../../../../types/amr-pipeline/models/Transaction';
import { compareDates } from '../../../../../utils/compare.utils';
import { roles } from '../../../../../constants';
import { user } from '../../../../../user';
import { amrPipelineService } from '../../../../../services/amr-pipeline.service';
import { dealsService } from '../../../../../services/deals.service';
import { filterDocuments, getDealDocLastDate } from '../../../../../utils/amr-pipeline.utils';

interface DocumentsProps {
    deal: Deal,
    transaction: Transaction;
}

export function Documents({ deal, transaction }: DocumentsProps) {
    const [searchTerm, setSearchTerm] = useState('');
    const isAdminOrDataEntry = user.hasRoles(roles.Administrator, roles.DataEntry);

    if (!deal.documents) return null;

    const originalDocuments = deal.documents.dealDocuments ?? [];
    const filteredDocuments = searchTerm
        ? filterDocuments(originalDocuments ?? [], searchTerm)
        : originalDocuments;

    const documents = filteredDocuments
        .filter(a => isAdminOrDataEntry || a.isPublic)
        .sort((a, b) => compareDates(getDealDocLastDate(b), getDealDocLastDate(a)));

    const amrDocuments = documents.filter(d => d.transactionReferenceName != null);
    const dealDocuments = documents.filter(d => d.transactionReferenceName == null);
    const dealRegularDocuments = dealDocuments.filter(d => getDealRegularDocumentTypes().some(t => t === d.documentType));
    const distributionReports = dealDocuments.filter(d => d.documentType === DocumentType.distributionReport);
    const monthlyReports = dealDocuments.filter(d => d.documentType === DocumentType.monthlyReport);
    const otherDocuments = dealDocuments.filter(d => d.documentType === DocumentType.otherDocument);

    const getEmptyPlaceholder = () => searchTerm.length > 0 ? 'No search results.' : 'There are no documents yet.'

    return (
        <TabBody className="tabs-data-padding component-tabs-deal-documents">
            <SearchInput
                onClear={() => setSearchTerm('')}
                value={searchTerm}
                onChange={setSearchTerm}
                placeholder="Search for documents"
            />
            <DocumentSection
                title="AMR Documents"
                dealReferenceName={deal.referenceName}
                transactionReferenceName={transaction.referenceName}
                items={amrDocuments}
                emptyPlaceholder={getEmptyPlaceholder()}
                downloadAllFetch={amrPipelineService.getTransactionDocuments}
                downloadAllArgs={[deal.referenceName, transaction.referenceName]}
                fileNameForSave={`${deal.ticker} AMR Documents.zip`}
            />
            <DocumentSection
                title="Deal Documents"
                dealReferenceName={deal.referenceName}
                items={dealRegularDocuments}
                emptyPlaceholder={getEmptyPlaceholder()}
                downloadAllFetch={dealsService.getDealRegularDocuments}
                downloadAllArgs={[deal.referenceName]}
                fileNameForSave={`${deal.ticker} Deal Documents.zip`}
            />
            <DocumentSection
                title="Distribution Reports"
                dealReferenceName={deal.referenceName}
                items={distributionReports}
                emptyPlaceholder={getEmptyPlaceholder()}
                downloadAllFetch={dealsService.getDealDocumentsByType}
                downloadAllArgs={[deal.referenceName, DocumentType.distributionReport]}
                fileNameForSave={`${deal.ticker} Distribution Reports.zip`}
            />
            <DocumentSection
                title="Monthly Reports"
                dealReferenceName={deal.referenceName}
                items={monthlyReports}
                emptyPlaceholder={getEmptyPlaceholder()}
                downloadAllFetch={dealsService.getDealDocumentsByType}
                downloadAllArgs={[deal.referenceName, DocumentType.monthlyReport]}
                fileNameForSave={`${deal.ticker} ${getDocumentTypeText(DocumentType.monthlyReport)}.zip`}
            />
            <DocumentSection
                title="Other Documents"
                dealReferenceName={deal.referenceName}
                items={otherDocuments}
                emptyPlaceholder={getEmptyPlaceholder()}
                downloadAllFetch={dealsService.getDealDocumentsByType}
                downloadAllArgs={[deal.referenceName, DocumentType.otherDocument]}
                fileNameForSave={`${deal.ticker} ${getDocumentTypeText(DocumentType.otherDocument)}.zip`}
            />
        </TabBody>
    );
}
